<template>
  <busy-button class="w-44"
               :variant="variant"
               :id="answerSaveButtonId"
               :is-loading="isBusy"
               :is-disabled="isNewAnswer || isDisabled"
               @click="saveAnswer"
  >
    {{ text }}
  </busy-button>
</template>

<script>
import toaster from '@/utils/toaster'
import BusyButton from '@/components/common/BusyButton'
import answerService from '@/services/amio/answer.service'
import answerModule from '@/store/modules/answer.module'
import conversationModule from '@/store/modules/conversation.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import intentModule from '@/store/modules/intent.module'
import previewModule from '@/store/modules/preview.module'
import templateModule from '@/store/modules/template.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import errorUtils from '@/utils/error.utils'
import clone from 'ramda/src/clone'
import intentService from '@/services/amio/intent.service'
import editorUtils from '@/utils/editor.utils'
import { ITEM_IDS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import botModule from '@/store/modules/bot.module'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import variablesModule from '@/store/modules/variables.module'

export default {
  name: 'AnswerSaveButton',

  components: {
    BusyButton
  },

  props: {
    botId: {
      type: String,
      required: true
    },
    isDisabled: {
      type: Boolean,
      required: false
    }
  },

  watch: {
    'answer.id'(newId, oldId) {
      window.clearTimeout(this.buttonStateTimeout)
      if (oldId !== ITEM_IDS.NEW) this.setButtonProps()
    }
  },

  data() {
    return {
      answerSaveButtonId: BUSY_BUTTONS.ANSWER_SAVE,
      archivedTemplateRefs: {},
      archivedVariableRefs: {},
      archivedAnswerRefs: {},
      buttonStateTimeout: null,
      variant: SAVE_BUTTON_VARIANTS.DEFAULT,
      text: SAVE_BUTTON_LABELS.DEFAULT
    }
  },

  computed: {
    answer: answerModule.getAnswer,
    conversationJsons: conversationModule.getJsons,
    error: previewModule.error,
    stateTree: chatBubbleModule.stateTree,
    answerList: answerModule.getAnswerList,
    templateRefs: templateModule.templateRefs,
    variableRefs: variablesModule.variableRefs,
    answerRefs: answerModule.answerRefs,
    answerExamplesCount: answerModule.getAnswerExamplesCount,
    answerExamples: answerModule.getAnswerExamples,
    intent: intentModule.getIntent,

    isEmailBot() {
      return botModule.isEmailBot()
    },

    isGptBot() {
      return botModule.isGptBot()
    },

    isBusy() {
      return busyButtonsModule.isButtonBusy(this.answerSaveButtonId)
    },

    isNewAnswer() {
      return this.answer?.id === ITEM_IDS.NEW
    },

    isEmptyStateTree() {
      return previewModule.isEmptyStateTree(this.stateTree)
    }
  },

  methods: {
    async saveAnswer() {
      if (this.answer.published && this.isEmptyStateTree) {
        toaster.error('The published answer cannot be empty.')
        return
      }

      this.saveTemplateRefs()
      this.saveVariableRefs()
      this.saveAnswerRefs()
      busyButtonsModule.setButtonBusy(this.answerSaveButtonId, true)
      this.error = null

      try {
        if (this.answer.id === ITEM_IDS.NEW) {
          const newAnswer = await editorUtils.createAnswer()
          this.$emit('process-new-answer', newAnswer)
        } else {
          if (editorUtils.detectPreviewChange()) await this.updateAnswer()
          if (this.isGptBot && editorUtils.detectIntentDescriptionChange()) await this.saveIntentDescription()
        }
      } catch (err) {
        // in case of failure, we should set references to state before saving started
        templateModule.restoreTemplateRefs(this.archivedTemplateRefs)
        variablesModule.restoreVariableRefs(this.archivedVariableRefs)
        answerModule.restoreAnswerRefs(this.archivedAnswerRefs)
        errorUtils.handleConversationDesignerError(err, this.botId, this.stateTree, 'Answer', this.$route.fullPath)
      } finally {
        this.startButtonStateTimeout()
        busyButtonsModule.setButtonBusy(this.answerSaveButtonId, false)
      }
    },

    async updateAnswer() {
      const patchData = {
        conversation_json: conversationModule.getRootJson(),
        conversation_json_hash: conversationModule.getRootJsonHash(),
        template_refs: this.archivedTemplateRefs,
        variable_refs: this.archivedVariableRefs,
        answer_refs: this.archivedAnswerRefs
      }
      const updatedAnswer = await answerService.update(this.botId, this.answer.id, patchData)
      conversationModule.setArchivedConversationJson(updatedAnswer.conversationJson)
      updatedAnswer.intent.examplesCount = this.answerExamplesCount
      answerModule.setAnswer({ ...updatedAnswer, examples: this.answerExamples })
      answerModule.setAnswerList(this.answerList.map(answer => answer.id === updatedAnswer.id ? updatedAnswer : answer))
      await conversationModule.setAnswersHashes({ [updatedAnswer.id]: updatedAnswer.conversationJsonHash })
    },

    async saveIntentDescription() {
      try {
        const updatedIntent = await intentService.update(this.intent.id, { description: this.intent.description || '' })
        intentModule.setIntent(updatedIntent)
        conversationModule.setArchivedIntentDescription(updatedIntent.description)
      } catch (err) {
        const errorMessage = err.response?.data?.details?.errors?.[0]?.message || err.message
        this.error = { description: errorMessage }
      } finally {
        this.startButtonStateTimeout()
        busyButtonsModule.setButtonBusy(this.answerSaveButtonId, false)
      }
    },

    // snapshot of template refs when save button is hit, to prevent reset of template refs after saving
    saveTemplateRefs() {
      this.archivedTemplateRefs = clone(this.templateRefs)
      templateModule.resetTemplateRefs()
    },

    saveVariableRefs() {
      this.archivedVariableRefs = clone(this.variableRefs)
      variablesModule.resetVariableRefs()
    },

    saveAnswerRefs() {
      this.archivedAnswerRefs = clone(this.answerRefs)
      answerModule.resetAnswerRefs()
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.variant = SAVE_BUTTON_VARIANTS[buttonState]
      this.text = SAVE_BUTTON_LABELS[buttonState]
    }
  }
}
</script>
